import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { contactsApi } from 'src/Services/API';
import { ContactDto, ContactPreviewDto } from 'src/Services/API/generated';
import { trackEvent } from 'src/Services/Matomo';

type FavoriteParams = {
  contactId: string;
  isFavorite: boolean;
};

export const useToggleFavorite = (
  options?: UseMutationOptions<ContactDto, unknown, FavoriteParams>
) => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();

  return useMutation<ContactDto, unknown, FavoriteParams>(
    ['toggle-favorite-contact', assetId],
    async ({ contactId, isFavorite }: FavoriteParams) => {
      if (!assetId) {
        throw new Error('No asset selected');
      }

      const response = await contactsApi.contactsControllerUpdateFavoriteStatus(
        contactId,
        assetId,
        { isFavorite }
      );

      return response.data;
    },
    {
      onSuccess: (_, { contactId }) => {
        queryClient.invalidateQueries('fetch-contacts');
        queryClient.invalidateQueries(['fetch-contact', assetId, contactId]);
        trackEvent('Contact', 'toggleFavorite');
      },
      onMutate: async ({ contactId, isFavorite }) => {
        await queryClient.cancelQueries(['fetch-contacts', assetId]);

        // save previous cache state
        const previousContacts = queryClient.getQueryData<{
          pages: { items: ContactPreviewDto[] }[];
        }>(['fetch-contacts', assetId]);

        // optimistic rendering of cache with new favorite status
        queryClient.setQueryData<{ pages: { items: ContactPreviewDto[] }[] }>(
          ['fetch-contacts', assetId],
          old => {
            if (!old) return { pages: [] };
            return {
              pages: old.pages.map(page => ({
                ...page,
                items: page.items.map(contact =>
                  contact.id === contactId ? { ...contact, isFavorite } : contact
                ),
              })),
            };
          }
        );

        // save previous cache state
        const previousContact = queryClient.getQueryData<ContactDto>(['fetch-contact', contactId]);

        // optimistic rendering of cache with new favorite status
        queryClient.setQueryData<ContactDto | undefined>(['fetch-contact', contactId], old => {
          const baseContact = old ?? previousContact;

          if (!baseContact) return undefined;

          return {
            ...baseContact,
            isFavorite,
          };
        });

        // return previous state in case of error
        return { previousContacts, previousContact };
      },
      ...options,
    }
  );
};
